import { createI18n } from 'vue-i18n'
import { ref } from 'vue'

import enUS from './en-US.json'
import zhCN from './zh-CN.json'

export type SupportedLocale = 'zh-CN' | 'en-US'
export type MessageTree = { [key: string]: string | MessageTree }

export const SUPPORTED_LOCALES = ['zh-CN', 'en-US'] as const
export const DEFAULT_LOCALE: SupportedLocale = 'zh-CN'

export const messages = {
  'zh-CN': zhCN,
  'en-US': enUS,
} satisfies Record<SupportedLocale, MessageTree>

const KNOWN_TEXT_KEYS: Record<string, string> = {
  '望舒': 'workbench.brand',
  '添加组件': 'workbench.topbar.addComponent',
  '切换到 AI 助手': 'workbench.topbar.switchToAiAssistant',
  '返回 AI 助手': 'workbench.topbar.switchToAiAssistant',
  '进入望舒AI助手': 'workbench.topbar.switchToAiAssistant',
  '进入望舒工作台': 'workbench.topbar.switchToWorkbench',
  '智能体总览': 'workbench.components.agentOverview.title',
  '决策中心': 'workbench.components.decisionCenter.title',
  '实时看板': 'workbench.components.realtimeBoard.title',
  '数据分析': 'workbench.components.dataAnalyst.title',
  '公告管理': 'workbench.components.notices.title',
  '智能客服会话队列': 'workbench.components.csConversations.title',
  '留存看板': 'workbench.components.retentionBoard.title',
  '智能体自动报告列表': 'workbench.components.analyticsReports.title',
  '用户分群': 'workbench.components.userSegments.title',
  'APP 推送任务': 'workbench.components.pushTasks.title',
  '违规事件队列': 'workbench.components.violations.title',
  '信息流广告任务': 'workbench.components.feedAdTasks.title',
  '分享配置策略': 'workbench.components.shareStrategies.title',
  '功能占位': 'workbench.components.stub.title',
  '功能开发中…': 'workbench.components.stub.body',
  '当前组件暂时不可用': 'workbench.components.stub.error',
  '数分智能体': 'workbench.components.stub.agentName',
  '跨工作台': 'workbench.groups.crossWorkbench',
  '抖音': 'workbench.groups.douyin',
  '用户': 'workbench.groups.user',
  '法务': 'workbench.groups.legal',
  '数据': 'workbench.groups.data',
  '工作中': 'workbench.components.agentOverview.working',
  '空闲': 'workbench.components.agentOverview.idle',
  '等待人工决策': 'workbench.components.agentOverview.awaiting',
  '已完成': 'workbench.status.completed',
  '排队中': 'workbench.status.queued',
  '审核中': 'workbench.status.reviewing',
  '已查阅': 'workbench.status.read',
  '待查阅': 'workbench.status.unread',
  '待发布': 'workbench.status.pendingPublish',
  '待决策': 'workbench.status.riskPending',
  '已处理': 'workbench.status.riskProcessed',
  '高': 'workbench.status.high',
  '中': 'workbench.status.medium',
  '低': 'workbench.status.low',
  '双开': 'workbench.status.doubleOpen',
  '倒金': 'workbench.status.goldTransfer',
  '养号': 'workbench.status.accountFarming',
  '工作室': 'workbench.status.studio'
}

export const currentLocale = ref<SupportedLocale>(resolveInitialLocale())

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: currentLocale.value,
  fallbackLocale: DEFAULT_LOCALE,
  messages,
})

export function normalizeLocale(value: string | null | undefined): SupportedLocale {
  const locale = String(value || '').trim().toLowerCase()
  if (!locale) {
    return DEFAULT_LOCALE
  }
  if (locale.includes('en')) {
    return 'en-US'
  }
  if (locale.includes('zh') || locale.includes('cn')) {
    return 'zh-CN'
  }
  return DEFAULT_LOCALE
}

export function resolveInitialLocale(): SupportedLocale {
  // 生产默认中文；持久化语言由 Wangshu preferences API hydrate，不使用平行本地 store。
  return DEFAULT_LOCALE
}

export function setLocale(locale: string): SupportedLocale {
  const normalized = normalizeLocale(locale)
  currentLocale.value = normalized
  i18n.global.locale.value = normalized
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', normalized)
  }
  return normalized
}

export function getLocale(): SupportedLocale {
  return currentLocale.value
}

function readMessage(locale: SupportedLocale, key: string): string | undefined {
  const parts = key.split('.')
  let node: string | MessageTree = messages[locale]
  for (const part of parts) {
    if (typeof node === 'string') {
      return undefined
    }
    node = node[part]
    if (node === undefined) {
      return undefined
    }
  }
  return typeof node === 'string' ? node : undefined
}

function interpolate(template: string, params: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(params[key] ?? `{${key}}`))
}

export function t(key: string, params?: Record<string, string | number>): string {
  const locale = currentLocale.value
  const template = readMessage(locale, key) ?? readMessage(DEFAULT_LOCALE, key) ?? key
  return interpolate(template, params)
}

export function translateKnownWorkbenchText(value: unknown): string {
  if (typeof value !== 'string') {
    return ''
  }
  const trimmed = value.trim()
  const key = KNOWN_TEXT_KEYS[trimmed]
  return key ? t(key) : trimmed
}

export default i18n
